import {
  AddressParts,
  Country,
  Currency,
  DemoAddress,
  Language,
  Locale,
  LocaleDataset,
  Subdivision,
  Timezone,
} from '@wix/locale-dataset-data/typings';
import { getFormattedAddress } from './address-format';
import * as datasetData from '@wix/locale-dataset-data';
import {
  DEFAULT_DEPRECATION_TIMESTAMPS,
  FORBIDDEN_COUNTRY_CODES,
} from './constants';

export interface GetAllCountriesOpts extends GetCountryOpts {
  filterForbiddenCountries?: boolean;
}

export interface GetCountryOpts {
  deprecationTimestamps?: number[];
}

export interface LocaleDatasetApi {
  getAllCountries(opts?: GetAllCountriesOpts): Country[];
  getCountryByKey(countryKey: string, opts?: GetCountryOpts): Country;
  getCountryByShortKey(countryShortKey: string, opts?: GetCountryOpts): Country;
  getStateByKey(countryKey: string, stateKey: string): Subdivision;
  getAllCurrencies(): Currency[];
  getCurrencyByKey(currencyKey: string): Currency;
  getAllLocales(): Locale[];
  getLocaleByKey(localeKey: string): Locale;
  getAllTimezones(): Timezone[];
  getTimezoneByKey(timezoneKey: string): Timezone;
  getFormattedAddress(
    addressParts: AddressParts,
    formatCountryKey?: string,
  ): string;
  getDemoAddresses(): DemoAddress[];
  getDemoAddressByKey(currencyKey: string): DemoAddress;
  getAllSupportedLanguages(): Language[];
  getAllLanguages(): Language[];
  getDefaultAddressDisplayNames(): AddressParts;
  mapReplacedSubdivision(subdivisionKey: string, countryKey: string): string;
  getRegionsReplacedByRegion(
    subdivisionKey: string,
    countryKey: string,
  ): string[];
}
const dataset: LocaleDataset = datasetData as LocaleDataset;

// Helpers
const clone = (item: any) => JSON.parse(JSON.stringify(item));
const all = (arr: any[]) => clone(arr);
const byKey = (arr: any[], key: string) => {
  const item = arr.filter((arrItem: any) => arrItem.key === key)[0];
  return item ? clone(item) : null;
};
const byShortKey = (arr: any[], shortKey: string) => {
  const item = arr.filter((arrItem: any) => arrItem.shortKey === shortKey)[0];
  return item ? clone(item) : null;
};
const withAddressPartDisplayNameDefaults = (country: Country): Country => {
  return (
    country && {
      ...country,
      addressPartDisplayNames: {
        ...dataset.addressPartDisplayNames,
        ...country.addressPartDisplayNames,
      },
    }
  );
};
const filteringDeprecatedSubdivisions = (deprecationTimestamps: number[]) => (
  country: Country,
): Country => {
  const { list: subdivs } = country?.subdivisions || {
    list: Array<Subdivision>(),
  };

  if (subdivs.length === 0) {
    return country;
  }

  const deprecatedSubDivsToShow = subdivs.filter(({ deprecatedOn }) =>
    deprecationTimestamps.includes(deprecatedOn),
  );
  const nonDeprecatedSubDivs = subdivs.filter(
    ({ deprecatedOn }) => !deprecatedOn,
  );
  const notReplacingOrReplaced = subdivs.filter(
    ({ replaces, replacedBy }) => !replaces?.length && !replacedBy?.length,
  );

  const list =
    deprecatedSubDivsToShow.length > 0
      ? deprecatedSubDivsToShow.concat(notReplacingOrReplaced)
      : nonDeprecatedSubDivs;

  return {
    ...country,
    subdivisions: {
      ...country.subdivisions,
      list,
    },
  };
};

const withCountryAdjustments = ({ deprecationTimestamps }: GetCountryOpts) => (
  country: Country,
): Country =>
  filteringDeprecatedSubdivisions(deprecationTimestamps)(
    withAddressPartDisplayNameDefaults(country),
  );

// Public API
export const getAllCountries = (opts: GetAllCountriesOpts = {}): Country[] => {
  const deprecationTimestamps =
    opts?.deprecationTimestamps || DEFAULT_DEPRECATION_TIMESTAMPS;

  return all(dataset.countries)
    .filter(
      (country) =>
        !opts.filterForbiddenCountries ||
        !FORBIDDEN_COUNTRY_CODES.includes(country.key),
    )
    .map(withCountryAdjustments({ deprecationTimestamps }));
};

export const getCountryByKey = (
  countryKey: string,
  { deprecationTimestamps }: GetCountryOpts = {
    deprecationTimestamps: DEFAULT_DEPRECATION_TIMESTAMPS,
  },
): Country =>
  withCountryAdjustments({ deprecationTimestamps })(
    byKey(dataset.countries, countryKey),
  );

export const getCountryByShortKey = (
  countryShortKey: string,
  { deprecationTimestamps }: GetCountryOpts = {
    deprecationTimestamps: DEFAULT_DEPRECATION_TIMESTAMPS,
  },
): Country =>
  withCountryAdjustments({ deprecationTimestamps })(
    byShortKey(dataset.countries, countryShortKey),
  );

export const getStateByKey = (
  countryKey: string,
  stateKey: string,
): Subdivision => {
  const country =
    byKey(dataset.countries, countryKey) ||
    byShortKey(dataset.countries, countryKey);
  return country && country.subdivisions
    ? byKey(country.subdivisions.list, stateKey)
    : null;
};

export const getAllCurrencies = (): Currency[] => all(dataset.currencies);
export const getCurrencyByKey = (currencyKey: string): Currency =>
  byKey(dataset.currencies, currencyKey);

export const getAllLocales = (): Locale[] => all(dataset.locales);
export const getLocaleByKey = (localeKey: string): Locale =>
  byKey(dataset.locales, localeKey.toLowerCase());

export const getAllTimezones = (): Timezone[] => all(dataset.timezones);
export const getTimezoneByKey = (timezoneKey: string): Timezone =>
  byKey(dataset.timezones, timezoneKey);

export const getDemoAddresses = (): DemoAddress[] => all(dataset.demoAddresses);
export const getDemoAddressByKey = (demoAddressKey: string): DemoAddress =>
  byKey(dataset.demoAddresses, demoAddressKey);

export const getAllSupportedLanguages = (): Language[] =>
  all(dataset.supportedLanguages);

export const getAllLanguages = (): Language[] => all(dataset.languages);

export const getDefaultAddressDisplayNames = (): AddressParts =>
  dataset.addressPartDisplayNames;

export const mapReplacedSubdivision = (
  subdivisionKey: string,
  countryKey: string,
) => getStateByKey(countryKey, subdivisionKey)?.replacedBy || subdivisionKey;

export const getRegionsReplacedByRegion = (
  subdivisionKey: string,
  countryKey: string,
) => getStateByKey(countryKey, subdivisionKey)?.replaces || [];

export default {
  getAllCountries,
  getCountryByKey,
  getCountryByShortKey,
  getStateByKey,
  getAllCurrencies,
  getCurrencyByKey,
  getAllLocales,
  getLocaleByKey,
  getAllTimezones,
  getTimezoneByKey,
  getFormattedAddress,
  getDemoAddresses,
  getDemoAddressByKey,
  getAllSupportedLanguages,
  getAllLanguages,
  getDefaultAddressDisplayNames,
  mapReplacedSubdivision,
  getRegionsReplacedByRegion,
} as LocaleDatasetApi;
